import { createSlice, PayloadAction } from '@reduxjs/toolkit';

import { RootState } from 'src/redux/index';
import {
  disableAutoJobApply,
  enableAutoJobApply,
  toggleBookmarkJob,
  withdrawJobApplication,
} from 'src/redux/actions/candidateLayout';
import { getLabel } from 'src/redux/actions/applicantReview';
import {
  deleteSkill,
  updatePreference,
} from 'src/redux/actions/candidateProfile';
import {
  claimDailyMissions,
  fetchDailyMissions,
} from 'src/redux/actions/rewards';
import {
  fetchJobs,
  fetchUserSelf,
  fetchAppliedJobs,
  getJobById,
  jobByIdOrAliasForCandidate,
  jobByIdForApplied,
  getSimilarJobs,
  getApplicantStatusInfo,
  markJobAsNotInterested,
  submitJobAssessment,
  fetchMyFeedbacks,
} from '../actions/candidateLayout';
import {
  CandidateState,
  UpdateApplicantSpotlightBalancePayload,
} from '../types/candidate';
import { highlightJob } from '../actions/competition';
import {
  questionAIAssistantQuery,
  setMultiplayerInvitePreference,
  theoryAiChat,
} from '../actions/skillBuilder';

const initialState: CandidateState = {
  isFetchingJobs: false,
  profileVersion: 0,
  isFetchingSimilarJobs: false,
  openCandidateFilter: false,
  level: {},
  candidateTourStepIdx: 0,
  isCandidateTourRunning: false,
  applicantInfoData: null,
  isFetchingAppliedJobs: false,
  appliedJobStatus: 'All',
};

/* eslint-disable no-param-reassign */
const candidateSlice = createSlice({
  name: 'candidate',
  initialState,
  reducers: {
    resetJobList: (state) => {
      state.jobList = undefined;
    },
    resetAppliedJobList: (state) => {
      state.jobAppliedList = undefined;
    },
    resetJobData: (state) => {
      state.jobData = undefined;
    },
    setOpenCandidateFilter: (state, action) => {
      state.openCandidateFilter = action.payload;
    },
    setIsCandidateTourRuning: (state, action: PayloadAction<boolean>) => {
      state.isCandidateTourRunning = action.payload;
    },
    setCandidateTourStepIdx: (state, action: PayloadAction<number>) => {
      state.candidateTourStepIdx = action.payload;
    },
    updateAppliedJobStatus: (state, action) => {
      state.appliedJobStatus = action.payload;
    },
    updateApplicantMetadata: (state, action) => {
      if (!state.applicantInfoData) return;

      state.applicantInfoData = {
        ...state.applicantInfoData,
        ...action.payload,
      };
    },
    updateApplicantSpotlightBalance: (
      state,
      action: PayloadAction<UpdateApplicantSpotlightBalancePayload>,
    ) => {
      const { key, delta } = action.payload;
      if (state.applicantInfoData?.spotlight) {
        state.applicantInfoData.spotlight[key] =
          (state.applicantInfoData.spotlight[key] || 0) + delta;
      }
    },
  },
  extraReducers: (builder) => {
    // Skills, roles and places are what every card's match is scored on: a list
    // fetched before the profile changed would keep the old scores, or none.
    builder.addCase(updatePreference.fulfilled, (state) => {
      state.jobList = undefined;
      state.profileVersion += 1;
    });
    builder.addCase(deleteSkill.fulfilled, (state) => {
      state.jobList = undefined;
      state.profileVersion += 1;
    });
    builder.addCase(fetchJobs.pending, (state) => {
      state.isFetchingJobs = true;
    });
    builder.addCase(fetchJobs.rejected, (state) => {
      state.isFetchingJobs = false;
    });
    builder.addCase(fetchJobs.fulfilled, (state, action) => {
      if (action.payload.current_page === 1) {
        state.jobList = action.payload;
      } else if (state.jobList?.jobs && action.payload.jobs) {
        state.jobList.jobs = [...state.jobList.jobs, ...action.payload.jobs];
        state.jobList.current_page = action.payload.current_page;
        state.jobList.total_pages = action.payload.total_pages;
      }
      state.isFetchingJobs = false;
    });
    builder.addCase(fetchAppliedJobs.pending, (state) => {
      state.isFetchingAppliedJobs = true;
    });
    builder.addCase(fetchAppliedJobs.fulfilled, (state, action) => {
      if ((action.payload.current_page ?? 0) <= 1) {
        state.jobAppliedList = action.payload;
      } else {
        state.jobAppliedList = {
          page_size: action.payload.page_size,
          total_pages: action.payload.total_pages,
          current_page: action.payload.current_page,
          jobs: [
            ...(state.jobAppliedList?.jobs ?? []),
            ...(action.payload.jobs ?? []),
          ],
        };
      }
      state.isFetchingAppliedJobs = false;
    });
    builder.addCase(fetchAppliedJobs.rejected, (state) => {
      state.isFetchingAppliedJobs = false;
    });

    builder.addCase(submitJobAssessment.fulfilled, (state, action) => {
      const { jobId } = action.meta.arg;
      if (action.payload.success && jobId) {
        if (state.jobAppliedList) {
          state.jobAppliedList.jobs = state.jobAppliedList?.jobs?.map((job) => {
            if (job.id === jobId) {
              return {
                ...job,
                is_assessment_pending: false,
              };
            }
            return job;
          });
        }
      }
    });

    builder.addCase(fetchUserSelf.fulfilled, (state, action) => {
      state.userSelf = action.payload;
    });
    builder.addCase(getJobById.fulfilled, (state, action) => {
      state.jobData = action.payload;
    });
    builder.addCase(jobByIdOrAliasForCandidate.fulfilled, (state, action) => {
      state.jobData = action.payload;
    });
    builder.addCase(jobByIdForApplied.fulfilled, (state, action) => {
      state.jobData = action.payload;
    });

    builder.addCase(fetchMyFeedbacks.pending, (state) => {
      state.isMyFeedbacksFetching = true;
    });
    builder.addCase(fetchMyFeedbacks.fulfilled, (state, action) => {
      state.isMyFeedbacksFetching = false;
      state.myFeedbacks = action.payload;
    });
    builder.addCase(fetchMyFeedbacks.rejected, (state) => {
      state.isMyFeedbacksFetching = false;
    });
    builder.addCase(toggleBookmarkJob.fulfilled, (state, action) => {
      const bookmarked = action.payload.is_bookmarked;
      if (state.jobData && state.jobData?.is_bookmarked !== undefined) {
        state.jobData.is_bookmarked = bookmarked;
      }
      if (state.jobList && bookmarked) {
        state.jobList.bookmarked_jobs_count =
          (state.jobList?.bookmarked_jobs_count || 0) + 1;
        state.jobList.jobs = state.jobList.jobs?.map((job) => {
          if (job.id === action.meta.arg.jobId) {
            return { ...job, is_bookmarked: bookmarked };
          }
          return job;
        });
      }
      if (state.jobList && !bookmarked) {
        state.jobList.bookmarked_jobs_count =
          (state.jobList?.bookmarked_jobs_count || 0) - 1;
      }
    });
    builder.addCase(getSimilarJobs.pending, (state) => {
      state.isFetchingSimilarJobs = true;
      state.similarJobs = undefined;
    });
    builder.addCase(getSimilarJobs.fulfilled, (state, action) => {
      state.similarJobs = {
        ...action.payload,
        jobs: [...(action.payload?.jobs ?? [])].sort(() => 0.5 - Math.random()),
      };
      state.isFetchingSimilarJobs = false;
    });
    builder.addCase(getSimilarJobs.rejected, (state) => {
      state.isFetchingSimilarJobs = false;
      state.similarJobs = { jobs: [] };
    });
    builder.addCase(getLabel.fulfilled, (state, action) => {
      state.level = action.payload;
    });
    builder.addCase(getApplicantStatusInfo.fulfilled, (state, action) => {
      state.applicantInfoData = action.payload;
    });
    builder.addCase(fetchDailyMissions.fulfilled, (state, action) => {
      if (!state.applicantInfoData) return;
      state.applicantInfoData.claimable_mission_count =
        action.payload.claimable_count ?? 0;
    });
    builder.addCase(claimDailyMissions.fulfilled, (state, action) => {
      if (!state.applicantInfoData) return;
      state.applicantInfoData.claimable_mission_count =
        action.payload.claimable_count ?? 0;
    });
    builder.addCase(questionAIAssistantQuery.fulfilled, (state, action) => {
      if (!state.applicantInfoData) return;
      const remaining = action.payload.remaining_ai_interactions;
      if (remaining !== undefined && remaining !== null) {
        state.applicantInfoData.remaining_ai_interactions = remaining;
        state.applicantInfoData.is_ai_assistant_unlocked = remaining > 0;
      }
    });
    builder.addCase(theoryAiChat.fulfilled, (state, action) => {
      if (!state.applicantInfoData) return;
      const remaining = action.payload?.remaining_ai_interactions;
      if (remaining !== undefined && remaining !== null) {
        state.applicantInfoData.remaining_ai_interactions = remaining;
        state.applicantInfoData.is_ai_assistant_unlocked = remaining > 0;
      }
    });
    builder.addCase(markJobAsNotInterested.fulfilled, (state, action) => {
      if (state.jobList && action.payload.success) {
        state.jobList.jobs = state.jobList?.jobs?.filter(
          (job) => job.id !== action.meta.arg.jobId,
        );
      }
    });
    builder.addCase(enableAutoJobApply.fulfilled, (state, action) => {
      state.applicantInfoData = {
        ...state.applicantInfoData,
        auto_job_apply_enabled: true,
        auto_apply_valid_until: action.payload?.auto_apply_valid_until,
      };
    });
    builder.addCase(
      setMultiplayerInvitePreference.fulfilled,
      (state, action) => {
        if (!state.applicantInfoData) return;
        state.applicantInfoData.multiplayer_invites_muted =
          action.payload?.multiplayer_invites_muted;
      },
    );
    builder.addCase(disableAutoJobApply.fulfilled, (state) => {
      state.applicantInfoData = {
        ...state.applicantInfoData,
        auto_job_apply_enabled: false,
      };
    });
    builder.addCase(withdrawJobApplication.fulfilled, (state, action) => {
      const withdrawnJobId = action.meta.arg.jobId;

      if (!state.jobAppliedList?.jobs) return;

      state.jobAppliedList.jobs = state.jobAppliedList.jobs.map((job) =>
        job.id === withdrawnJobId ? { ...job, is_withdrawn: true } : job,
      );
    });
    builder.addCase(highlightJob.fulfilled, (state, action) => {
      const jobId = action.meta.arg?.jobId;
      if (!jobId || !state.jobAppliedList?.jobs) return;

      state.jobAppliedList.jobs = state.jobAppliedList.jobs.map((job) =>
        job.id === jobId ? { ...job, is_profile_highlighted: true } : job,
      );
    });
  },
});
/* eslint-enable no-param-reassign */

const { reducer, actions } = candidateSlice;

export default reducer;
export const jobDataSelector = (state: RootState) => state.candidate.job;
export const {
  resetJobList,
  resetJobData,
  setOpenCandidateFilter,
  setIsCandidateTourRuning,
  setCandidateTourStepIdx,
  resetAppliedJobList,
  updateAppliedJobStatus,
  updateApplicantMetadata,
  updateApplicantSpotlightBalance,
} = actions;
